import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../firebase/firebase";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "../translations";
import { updateStoreSettings } from "../../../../firebase/settings-functions";
import "./PaymentTab.css";

const PaymentSettings = ({ storeData, handleChange, storeId, setStoreData }) => {
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);

    const [stripeConnectStatus, setStripeConnectStatus] = useState({
        isConnected: false,
        accountId: null,
        chargesEnabled: false,
        payoutsEnabled: false
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [paymentMethodsSuccess, setPaymentMethodsSuccess] = useState("");

    useEffect(() => {
        fetchStripeConnectStatus();
    }, [storeId]);

    const fetchStripeConnectStatus = async () => {
        setLoading(true);
        setError("");

        try {
            const getStripeConnectStatus = httpsCallable(functions, "getStripeConnectStatus");
            const result = await getStripeConnectStatus({ storeId });

            if (result.data.success) {
                setStripeConnectStatus(result.data);
            }
        } catch (err) {
            console.error("Error fetching Stripe Connect status:", err);
            setError(t("failedToLoadStripeConfig") || "Failed to load Stripe connection status");
        } finally {
            setLoading(false);
        }
    };

    const handleConnectStripe = async () => {
        setSaving(true);
        setError("");
        setSuccess("");

        try {
            const createOAuthLink = httpsCallable(functions, "createStripeConnectOAuthLink");
            const result = await createOAuthLink({ storeId });

            if (result.data.success) {
                window.location.href = result.data.authUrl; // Fixed: was accountLinkUrl
            } else {
                setError(result.data.error || "Failed to create Stripe connection link");
            }
        } catch (err) {
            console.error("Error connecting Stripe:", err);
            setError(err.message || "Failed to initiate Stripe connection");
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnectStripe = async () => {
        setSaving(true);
        setError("");

        try {
            const disconnectStripe = httpsCallable(functions, "disconnectStripeConnect");
            const result = await disconnectStripe({ storeId });

            if (result.data.success) {
                setStripeConnectStatus({
                    isConnected: false,
                    accountId: null,
                    chargesEnabled: false,
                    payoutsEnabled: false
                });
                setSuccess("Stripe account disconnected successfully!");

                // Update store data to reflect disconnection
                setStoreData({
                    ...storeData,
                    stripeConnected: false,
                    paymentMethods: {
                        ...storeData.paymentMethods,
                        creditCard: false
                    }
                });
            }
        } catch (err) {
            console.error("Error disconnecting Stripe:", err);
            setError(err.message || "Failed to disconnect Stripe account");
        } finally {
            setSaving(false);
        }
    };

    const handleSavePaymentMethods = async () => {
        if (!storeId) {
            setError("Store ID is not configured.");
            return;
        }

        setSavingPaymentMethods(true);
        setPaymentMethodsSuccess("");
        setError("");

        try {
            await updateStoreSettings(storeId, {
                paymentMethods: storeData.paymentMethods,
                directBankTransferDetails: storeData.directBankTransferDetails
            });

            setPaymentMethodsSuccess(t('paymentMethodsSaved') || "Payment methods saved successfully!");

            setTimeout(() => {
                setPaymentMethodsSuccess("");
            }, 3000);
        } catch (err) {
            console.error("Error saving payment methods:", err);
            setError(err.message || t('failedToSavePaymentMethods') || "Failed to save payment methods");
        } finally {
            setSavingPaymentMethods(false);
        }
    };

    if (loading) {
        return <div>Loading payment settings...</div>;
    }

    return (
        <div className="payment-settings">
            <h3 className="section-title">Payment Methods</h3>
            <p className="section-help">Configure how customers can pay for their orders</p>

            {/* Stripe Connect Section */}
            <div className="stripe-connect-section">
                <h4 className="subsection-title">Online Payments (Credit Card)</h4>
                <p className="section-help">
                    Connect your Stripe account to accept credit card payments online
                </p>

                {!stripeConnectStatus.isConnected ? (
                    <div className="stripe-connect-setup">
                        <div className="connect-info">
                            <div className="connect-icon">💳</div>
                            <div className="connect-text">
                                <h5>Connect Stripe Account</h5>
                                <p>Connect your existing Stripe account or create a new one to start accepting online payments</p>
                            </div>
                        </div>
                        <button
                            className="stripe-connect-btn primary"
                            onClick={handleConnectStripe}
                            disabled={saving}
                        >
                            {saving ? 'Connecting...' : 'Connect with Stripe'}
                        </button>
                    </div>
                ) : (
                    <div className="stripe-connected">
                        <div className="connection-status">
                            <div className="status-indicator connected">✓</div>
                            <div className="status-text">
                                <h5>Stripe Connected</h5>
                                <p>Account ID: {stripeConnectStatus.accountId}</p>
                            </div>
                        </div>

                        <div className="stripe-status-grid">
                            <div className="status-item">
                                <span className="status-label">Charges:</span>
                                <span className={`status-value ${stripeConnectStatus.chargesEnabled ? 'enabled' : 'disabled'}`}>
                                    {stripeConnectStatus.chargesEnabled ? '✅ Enabled' : '❌ Disabled'}
                                </span>
                            </div>
                            <div className="status-item">
                                <span className="status-label">Payouts:</span>
                                <span className={`status-value ${stripeConnectStatus.payoutsEnabled ? 'enabled' : 'disabled'}`}>
                                    {stripeConnectStatus.payoutsEnabled ? '✅ Enabled' : '❌ Disabled'}
                                </span>
                            </div>
                        </div>

                        <div className="stripe-actions">
                            <button
                                className="stripe-connect-btn secondary"
                                onClick={handleDisconnectStripe}
                                disabled={saving}
                            >
                                {saving ? 'Disconnecting...' : 'Disconnect Stripe Account'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Payment Methods Configuration */}
            <div className="payment-methods-section">
                <h4 className="subsection-title">Available Payment Methods</h4>
                <div className="payment-method-options">
                    <div className="payment-method-item">
                        <input
                            type="checkbox"
                            id="creditCard"
                            name="paymentMethods.creditCard"
                            checked={storeData.paymentMethods.creditCard && stripeConnectStatus.isConnected && stripeConnectStatus.chargesEnabled}
                            onChange={handleChange}
                            disabled={!stripeConnectStatus.isConnected || !stripeConnectStatus.chargesEnabled}
                        />
                        <label htmlFor="creditCard">
                            {t('creditCard') || 'Credit Card'}
                            {(!stripeConnectStatus.isConnected || !stripeConnectStatus.chargesEnabled) && (
                                <span className="method-note"> (Connect Stripe first)</span>
                            )}
                        </label>
                    </div>

                    <div className="payment-method-item">
                        <input
                            type="checkbox"
                            id="cashOnDelivery"
                            name="paymentMethods.cashOnDelivery"
                            checked={storeData.paymentMethods.cashOnDelivery}
                            onChange={handleChange}
                        />
                        <label htmlFor="cashOnDelivery">{t('cashOnDelivery') || 'Cash on Delivery'}</label>
                    </div>

                    <div className="payment-method-item">
                        <input
                            type="checkbox"
                            id="directBankTransfer"
                            name="paymentMethods.directBankTransfer"
                            checked={storeData.paymentMethods.directBankTransfer}
                            onChange={handleChange}
                        />
                        <label htmlFor="directBankTransfer">{t('directBankTransfer') || 'Direct Bank Transfer'}</label>
                    </div>
                </div>

                {/* Bank Transfer Details Section */}
                {storeData.paymentMethods.directBankTransfer && (
                    <div className="bank-transfer-details-section">
                        <h5 className="bank-details-title">Bank Transfer Details</h5>
                        <p className="section-help">Enter your bank details that customers will see during checkout</p>

                        <div className="bank-details-form">
                            <div className="form-group">
                                <label htmlFor="bankDetailsEn">Bank Details (English) *</label>
                                <textarea
                                    id="bankDetailsEn"
                                    name="directBankTransferDetails.en"
                                    value={storeData.directBankTransferDetails.en}
                                    onChange={handleChange}
                                    placeholder="Enter bank details in English&#10;Example:&#10;Bank Name: XYZ Bank&#10;Account Name: Your Store Name&#10;Account Number: 1234567890&#10;IBAN: AE123456789012345678901&#10;Swift Code: XYZAEAD"
                                    rows="6"
                                    className="bank-details-textarea"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="bankDetailsAr">Bank Details (Arabic)</label>
                                <textarea
                                    id="bankDetailsAr"
                                    name="directBankTransferDetails.ar"
                                    value={storeData.directBankTransferDetails.ar}
                                    onChange={handleChange}
                                    placeholder="أدخل تفاصيل البنك بالعربية&#10;مثال:&#10;اسم البنك: بنك XYZ&#10;اسم الحساب: اسم متجرك&#10;رقم الحساب: 1234567890&#10;الآيبان: AE123456789012345678901&#10;رمز السويفت: XYZAEAD"
                                    rows="6"
                                    className="bank-details-textarea"
                                    dir="rtl"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Save Payment Methods Button */}
                <div className="settings-form-actions" style={{ marginTop: '30px' }}>
                    {paymentMethodsSuccess && <div className="admin-success-message">{paymentMethodsSuccess}</div>}
                    {error && <div className="admin-error-message">{error}</div>}
                    {success && <div className="admin-success-message">{success}</div>}
                    <button
                        type="button"
                        className="save-btn"
                        onClick={handleSavePaymentMethods}
                        disabled={savingPaymentMethods}
                    >
                        {savingPaymentMethods ? "Saving..." : t('savePaymentMethods') || "Save Payment Methods"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentSettings;